import {
  ORG_CITY_MAX,
  ORG_LEGAL_NAME_MAX,
  ORG_MAX_MEMBERS_CAP,
  ORG_NAME_MAX,
} from '@carcheck/shared';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';
import {
  useWatch,
  type Control,
  type FieldErrors,
  type UseFormRegister,
  type UseFormSetValue,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { PlaceSelect } from '../../../components/place';
import type { CompanyFormValues } from '../_lib/companyForm';

/**
 * The company fields (AUT-403), after `companies-create.html`: «Компания»
 * (name, country + city, legal name, УНП), «Контакты в отчёте» (phone, Telegram,
 * site) and «Лимит мест». Shared by the create page and the edit dialog;
 * the parent owns the RHF form. No «дневной лимит» (decision 10).
 */
type FieldName = keyof CompanyFormValues;

const MAX: Partial<Record<FieldName, number>> = {
  name: ORG_NAME_MAX,
  city: ORG_CITY_MAX,
  legalName: ORG_LEGAL_NAME_MAX,
  maxMembers: ORG_MAX_MEMBERS_CAP,
};

export interface CompanyFormFieldsProps {
  register: UseFormRegister<CompanyFormValues>;
  control: Control<CompanyFormValues>;
  setValue: UseFormSetValue<CompanyFormValues>;
  errors: FieldErrors<CompanyFormValues>;
  disabled?: boolean;
}

const grid = (columns: string) =>
  ({ display: 'grid', gridTemplateColumns: { xs: '1fr', md: columns }, gap: 2 }) as const;

const SectionTitle = ({ title, hint }: { title: string; hint?: string }): ReactElement => (
  <Typography variant="subtitle2" fontWeight={700}>
    {title}
    {hint ? (
      <Typography component="span" variant="caption" color="text.secondary">
        {' '}
        · {hint}
      </Typography>
    ) : null}
  </Typography>
);

export const CompanyFormFields = ({
  register,
  control,
  setValue,
  errors,
  disabled = false,
}: CompanyFormFieldsProps): ReactElement => {
  const { t } = useTranslation();
  const [cityCountry = '', city = '', cityCustom = false] = useWatch({
    control,
    name: ['cityCountry', 'city', 'cityCustom'],
  });
  const place = { cityCountry, city, cityCustom };
  const f = (name: FieldName, hintKey?: string) => {
    const error = errors[name];
    return {
      ...register(name),
      id: `company-${name}`,
      slotProps: { htmlInput: { 'data-testid': `company-${name}-input` } },
      size: 'small' as const,
      fullWidth: true,
      disabled,
      label: t(`companies.form.${name}`),
      error: Boolean(error),
      helperText: error
        ? t(error.message ?? '', { max: MAX[name] })
        : hintKey
          ? t(hintKey)
          : undefined,
    };
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <SectionTitle title={t('companies.form.sections.company')} />
      <Box sx={grid('1fr 1fr')}>
        <TextField {...f('name', 'companies.form.nameHint')} required />
        <TextField {...f('legalName')} />
        <TextField {...f('unp', 'companies.form.unpHint')} inputMode="numeric" />
      </Box>
      <Box sx={grid('1fr 1fr')}>
        <PlaceSelect
          mode="form"
          testId="company-place"
          value={place}
          disabled={disabled}
          customMax={ORG_CITY_MAX}
          onChange={(next) => {
            const opts = { shouldDirty: true, shouldValidate: true };
            setValue('cityCountry', next.cityCountry, opts);
            setValue('city', next.city, opts);
            setValue('cityCustom', next.cityCustom, opts);
          }}
        />
      </Box>

      <SectionTitle
        title={t('companies.form.sections.contacts')}
        hint={t('companies.form.sections.contactsHint')}
      />
      <Box sx={grid('1fr 1fr 1fr')}>
        <TextField {...f('phone')} inputMode="tel" />
        <TextField
          {...f('telegram')}
          slotProps={{
            htmlInput: { 'data-testid': 'company-telegram-input' },
            input: { startAdornment: <InputAdornment position="start">@</InputAdornment> },
          }}
        />
        <TextField {...f('site')} />
      </Box>
      <Typography variant="caption" color="text.secondary">
        {t('companies.form.contactsNote')}
      </Typography>

      <SectionTitle title={t('companies.form.sections.seats')} />
      <Box sx={{ maxWidth: 220 }}>
        <TextField {...f('maxMembers', 'companies.form.maxMembersHint')} inputMode="numeric" />
      </Box>
    </Box>
  );
};
